import { useActionListContext } from '@latitude/action-list';
import { Icon } from '@latitude/icons';
import { DebouncedInput } from '@latitude/input';
import { cn } from '@latitude/token-utils';
import type { KeyboardEvent } from 'react';
import { useCallback, useMemo } from 'react';
import type { TriggerInputProps } from 'types';

import { Tag } from './Tag';

/**
 * A component that represents a trigger input.
 *
 * @component
 * @example
 * ```tsx
 * <TriggerInput
 *   className="my-input"
 *   trailingElement="Search"
 *   direction="down"
 *   onChange={handleInputChange}
 * />
 * ```
 */

const iconClass =
  '[&>*]:h-sizing-comp-action-list-icon [&>*]:w-sizing-comp-action-list-icon';

export const TriggerInput = <
  isMulti extends boolean = false,
  isNullable extends boolean = false,
  T = undefined,
>({
  classNames,
  trailingElement,
  direction,
  isMulti,
  isNullable,
  ref,
  disabled,
  readOnly,
  clickOnly,
  multiSelectOverflow = 'overflowStack',
  hasMaxHeight = true,
  onBlur,
  placeholder,
  'aria-label': ariaLabel,
  ...props
}: TriggerInputProps<isMulti, isNullable>) => {
  const { optionMap, selectedValues, onChange } = useActionListContext<
    isMulti,
    isNullable,
    T
  >();

  // Trigger actions (clear, backspace, tag remove) call onBlur after onChange.
  // This is needed because react-hook-form's mode: 'onBlur' only validates on
  // blur events. Without this, removing the last tag would leave the field
  // invalid without showing an error until the next popover open/close cycle.
  // Unlike option clicks in the dropdown (which are additive), trigger actions
  // are destructive removals that can make the field invalid.
  const onClearHandler = useCallback(() => {
    onChange(undefined);
    onBlur?.();
  }, [onChange, onBlur]);

  const onInputKeyDownHandler = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!isMulti || !Array.isArray(selectedValues)) {
        return;
      }

      if (e.code === 'Backspace' && 'value' in e.target && !e.target.value) {
        if (selectedValues.length > 0) {
          onChange(selectedValues.at(-1));
          onBlur?.();
        }
      }
    },
    [selectedValues, isMulti, onChange, onBlur],
  );

  const Tags = useMemo(() => {
    if (!isMulti || !selectedValues || !Array.isArray(selectedValues)) {
      return null;
    }

    const noShrink = multiSelectOverflow === 'overflowScroll';

    return selectedValues.map((val) => {
      const { label, value } = optionMap.get(val) || {};

      if (!label) {
        return null;
      }

      return (
        <Tag
          key={`${label}-${value}`}
          onRemove={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onChange(val);
            onBlur?.();
          }}
          readOnly={readOnly}
          disabled={disabled}
          noShrink={noShrink}
        >
          {label}
        </Tag>
      );
    });
  }, [
    selectedValues,
    optionMap,
    multiSelectOverflow,
    readOnly,
    disabled,
    isMulti,
    onChange,
    onBlur,
  ]);

  const [label, leadingIcon] = useMemo(() => {
    if (!selectedValues || isMulti || Array.isArray(selectedValues)) {
      return ['', undefined];
    }

    const selectedOption = optionMap.get(selectedValues);
    return [
      selectedOption?.label ?? '',
      selectedOption?.leadingIcon ?? undefined,
    ];
  }, [optionMap, selectedValues, isMulti]);

  const leadingItemElement = useMemo(
    () =>
      !isMulti && leadingIcon ? (
        <span className={iconClass}>{leadingIcon}</span>
      ) : null,
    [leadingIcon, isMulti],
  );

  const chipsContainerClassName = useMemo(() => {
    const base = classNames?.contentWrapper || '';

    if (!isMulti) {
      return base;
    }

    if (multiSelectOverflow === 'overflowScroll') {
      // Horizontal scroll mode: single line with horizontal scroll (scrollbar hidden)
      return `${base} [&>div]:!flex-nowrap [&>div]:!overflow-x-auto [&>div]:no-scrollbar`;
    }

    // overflowStack mode with maxHeight: apply overflow-y and max-height via CSS variable
    if (hasMaxHeight) {
      // Use CSS variable --chips-max-height set on the parent TriggerWrapper
      return `${base} [&>div]:!overflow-y-auto [&>div]:!max-h-[var(--chips-max-height)]`;
    }

    return base;
  }, [isMulti, multiSelectOverflow, hasMaxHeight, classNames?.contentWrapper]);

  return (
    <DebouncedInput
      ref={ref}
      inputSize="small"
      classNames={{
        ...classNames,
        contentWrapper: chipsContainerClassName,
      }}
      rawProps={{
        role: 'combobox',
        onKeyDown: onInputKeyDownHandler,
        ...(clickOnly && { tabIndex: -1 }),
        ...((ariaLabel ?? placeholder) && {
          'aria-label': ariaLabel ?? placeholder,
        }),
      }}
      onClear={onClearHandler}
      chipsContainer={Tags}
      value={label}
      disabled={disabled}
      placeholder={placeholder}
      leadingElement={leadingItemElement}
      trailingElement={
        <span
          className={cn({
            flex: true,
            'items-center': true,
            'cursor-not-allowed': disabled,
          })}
        >
          {trailingElement && (
            <span className={iconClass}>{trailingElement}</span>
          )}
          {!readOnly &&
            (!isMulti && isNullable && label.length > 0 ? (
              <Icon
                name="x-close"
                className="w-sizing-sys-size-24 h-sizing-sys-size-24"
                onClick={(
                  e: React.MouseEvent<HTMLOrSVGElement, MouseEvent>,
                ) => {
                  e.stopPropagation();
                  onClearHandler();
                }}
              />
            ) : (
              <Icon
                name="chevron-left"
                className="w-sizing-sys-size-24 h-sizing-sys-size-24"
                direction={direction}
              />
            ))}
        </span>
      }
      readOnly={clickOnly}
      {...props}
    />
  );
};

TriggerInput.displayName = 'Input';
